import type { ISessionData } from '@/contexts/AppContext/AppContext';
import { NOTIFICATION_CACHE_KEY } from '@/contexts/AppContext/AppContext';
import type { ContentType } from '@/interfaces';
import SessionStorage from '@/services/SessionStorage';
import { abbreviate } from '@/utils/strings';
import type { Context } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { FCh } from 'react-fc';

export type QuickSearchShortcut = {
    matches: string[]; // all the strings to match against
    title: string; // the main title to display
    parentSection: string; // some context of where the link came from
    url: string; // the destination
};

const emptyContextData: INavigationContextValue = {
    notificationBadge: undefined,
    availableNavigationLinks: undefined,
    navSections: undefined,
    isMenuOpen: false,
    isDesktopQuickSearchVisible: false,
    isAccountMenuOpen: false,
    visibleHoverMenuIndex: null,
    hoverMenuOffset: 0,
    closeMenu: () => ({}),
    toggleMenu: () => ({}),
    openHoverMenu: () => ({}),
    openAccountMenu: () => ({}),
    openQuickSearch: () => ({}),
    modifySavedItemCount: () => {},
    clearNotificationBadge: () => {},
};

const buildShortcutLink = (link: NavigationLink, ...parents: string[]): QuickSearchShortcut => {
    const matches = [link.label.toLowerCase().trim()];
    // Also add the abbreviation (to start of array so it shows first).
    const abbr = abbreviate(link.label);
    if (abbr.length > 1) {
        matches.unshift(abbr.toLowerCase());
    }
    parents = [...new Set(parents || [])]; // remove duplicates
    // The ancestors should also yield the items.
    parents?.forEach((parent) => {
        matches.push(parent.toLowerCase());
    });
    return {
        matches,
        title: link.label,
        parentSection: parents.join(' > '),
        url: link.href,
    };
};

const buildShortcutLinks = (sections: NavigationSection[]) => {
    const targets: QuickSearchShortcut[] = [];
    const seenUrls: Set<string> = new Set();

    sections.forEach((section) => {
        if (!section.columns) {
            return;
        }
        section.columns.forEach((column) => {
            column.mainLinks?.forEach((link) => {
                if (seenUrls.has(link.href)) {
                    return;
                }
                targets.push(buildShortcutLink(link, section.title, column.title));
                seenUrls.add(link.href);
            });
            column.subLinks?.forEach((link) => {
                if (seenUrls.has(link.href)) {
                    return;
                }
                targets.push(buildShortcutLink(link, section.title, column.title));
                seenUrls.add(link.href);
            });
            column.subColumns?.forEach((subColumn) => {
                subColumn.links.forEach((link) => {
                    if (seenUrls.has(link.href)) {
                        return;
                    }
                    targets.push(buildShortcutLink(link, section.title, column.title, subColumn.title));
                    seenUrls.add(link.href);
                });
            });
        });
    });
    return targets;
};

export const NavigationContext: Context<INavigationContextValue> = createContext(emptyContextData);

export const NavigationContextProvider: FCh<IProps> = ({ notificationBadge, navSections, children }) => {
    const [notificationBadgeData, setNotificationBadgeData] = useState(notificationBadge);
    const [isMenuOpen, setIsMenuOpen] = useState(undefined); // initial load has no state (no-js fallback)
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [visibleHoverMenuIndex, setVisibleHoverMenu] = useState(null);
    const [isDesktopQuickSearchVisible, setIsDesktopQuickSearchVisible] = useState(false);
    const [hoverMenuOffset, setHoverMenuOffset] = useState(0);

    useEffect(() => {
        setNotificationBadgeData(notificationBadge);
    }, [notificationBadge]);

    const availableNavigationLinks = useMemo(() => (navSections ? buildShortcutLinks(navSections) : []), [navSections]);

    return (
        <NavigationContext.Provider
            value={{
                navSections,
                availableNavigationLinks,
                notificationBadge: notificationBadgeData,
                isDesktopQuickSearchVisible,
                isMenuOpen,
                isAccountMenuOpen,
                visibleHoverMenuIndex,
                hoverMenuOffset,
                closeMenu: () => {
                    if (document.querySelectorAll('dialog[open]').length > 0) {
                        // don't close the nav bar (unmount menu item) if there's an open dialog
                        // otherwise, when mouse goes outside the browser window,
                        // we may unmount ContactFormLink which would unmount the ContactUs dialog
                        return;
                    }
                    setIsMenuOpen(false);
                    setIsAccountMenuOpen(false);
                    setVisibleHoverMenu(null);
                    setIsDesktopQuickSearchVisible(false);
                },
                toggleMenu: () => {
                    setVisibleHoverMenu(null);
                    setIsMenuOpen(!isMenuOpen);
                    setIsDesktopQuickSearchVisible(false);
                },
                openHoverMenu: (index, elmRef) => {
                    setHoverMenuOffset(
                        elmRef.getBoundingClientRect().left -
                            document.getElementById('logo').getBoundingClientRect().left
                    );
                    setVisibleHoverMenu(index);
                    setIsDesktopQuickSearchVisible(false);
                    setIsAccountMenuOpen(false);
                },
                openQuickSearch: () => {
                    setHoverMenuOffset(0);
                    setVisibleHoverMenu(null);
                    setIsDesktopQuickSearchVisible(true);
                    setIsAccountMenuOpen(false);
                },
                openAccountMenu: () => {
                    setHoverMenuOffset(0);
                    setVisibleHoverMenu(null);
                    setIsDesktopQuickSearchVisible(false);
                    setIsAccountMenuOpen(true);
                },
                modifySavedItemCount: (changeBy: number) => {
                    setNotificationBadgeData((prev) => {
                        SessionStorage.remove(NOTIFICATION_CACHE_KEY); // clear cache so it will be reloaded
                        return {
                            ...prev,
                            savedItemCount: Math.max(0, (prev?.savedItemCount ?? 0) + changeBy),
                            totalCount: Math.max(0, (prev?.totalCount ?? 0) + changeBy),
                        };
                    });
                },
                clearNotificationBadge: () => SessionStorage.remove(NOTIFICATION_CACHE_KEY),
            }}
        >
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigationContext = (): INavigationContextValue => {
    return useContext(NavigationContext); // once everything is within a context add back the "throw" if not set
};

export type NavigationSection = {
    title: string;
    columns: NavigationColumn[];
};
export type NavigationColumn = {
    title: string;
    theme?: ContentType;
    intro?: string;
    inlineHeader?: boolean;
    mainLinks?: (NavigationLink & { intro?: string })[];
    subLinks?: NavigationLink[];
    subColumns?: {
        title: string;
        links: NavigationLink[];
    }[];
};
export type NavigationLink = {
    label: string;
    href: string;
    newUntil?: string; // isoDate
};

interface INavigationContextValue {
    navSections: NavigationSection[];
    notificationBadge: ISessionData['notificationBadge'];
    availableNavigationLinks: QuickSearchShortcut[];
    isMenuOpen: boolean;
    isDesktopQuickSearchVisible: boolean;
    isAccountMenuOpen: boolean;
    visibleHoverMenuIndex?: number;
    hoverMenuOffset: number;
    closeMenu: () => void;
    toggleMenu: () => void;
    openQuickSearch: () => void;
    openAccountMenu: () => void;
    openHoverMenu: (index: number, elmRef: HTMLButtonElement | HTMLAnchorElement) => void;
    modifySavedItemCount: (changeBy: number) => void;
    clearNotificationBadge: () => void;
}

interface IProps {
    navSections: NavigationSection[];
    notificationBadge?: ISessionData['notificationBadge'];
}
